import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../components/layout/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function Login() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { email: "", password: "" } });

  const onSubmit = async (values) => {
    try {
      const user = await login(values.email.trim(), values.password);
      if (user.role !== "client") {
        logout();
        toast.error("This is the investor portal. Please use the admin sign-in page.");
        navigate("/admin/login");
        return;
      }
      toast.success(`Welcome back, ${user.name?.split(" ")[0] || "investor"}`);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(apiError(error, "Unable to sign in"));
    }
  };

  return (
    <AuthLayout
      kicker="Investor portal"
      title="Sign in to your account"
      subtitle="Access your portfolio, returns and withdrawal schedule."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register("email")}
            data-testid="login-email-input"
          />
          {errors.email ? (
            <p className="text-xs text-destructive" data-testid="login-email-error">
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              data-testid="login-forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              className="pr-10"
              {...register("password")}
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors hover:text-foreground"
              aria-label={show ? "Hide password" : "Show password"}
              data-testid="login-password-toggle"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password ? (
            <p className="text-xs text-destructive" data-testid="login-password-error">
              {errors.password.message}
            </p>
          ) : null}
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="login-submit-button">
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="mr-2 h-4 w-4" />
          )}
          Sign in
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Accounts are created by your relationship manager. Contact support if you need access.
        </p>
        <p className="text-center text-xs">
          <Link
            to="/admin/login"
            className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            data-testid="login-admin-link"
          >
            Administrator sign-in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
